import "./Calendar.css";
import { useAuth } from "../../config/utilities/useAuth";
import PropTypes from "prop-types";
import LightBlueDot from "./img/dots/lightBlueDot.svg";
import OrangeDot from "./img/dots/orangeDot.svg";
import PurpleDot from "./img/dots/purpleDot.svg";
import BlueDot from "./img/dots/blueDot.svg";
import YellowDot from "./img/dots/yellowDot.svg";
import LightBlueBus from "./img/bus/lightBlueBus.svg";
import OrangeBus from "./img/bus/orangeBus.svg";
import PurpleBus from "./img/bus/purpleBus.svg";
import BlueBus from "./img/bus/blueBus.svg";
import YellowBus from "./img/bus/yellowBus.svg";
import LightBlueAssistant from "./img/assistant/lightBlueAssistant.svg";
import OrangeAssistant from "./img/assistant/orangeAssistant.svg";
import PurpleAssistant from "./img/assistant/purpleAssistant.svg";
import BlueAssistant from "./img/assistant/blueAssistant.svg";
import YellowAssistant from "./img/assistant/yellowAssistant.svg";
import LightBlueDriver from "./img/driver/lightBlueDriver.svg";
import OrangeDriver from "./img/driver/orangeDriver.svg";
import PurpleDriver from "./img/driver/purpleDriver.svg";
import BlueDriver from "./img/driver/blueDriver.svg";
import YellowDriver from "./img/driver/yellowDriver.svg";

export default function CalendarItem({
  data,
  compact = true,
  onClick,
  remainingCount = 0,
}) {
  const { tripTypes } = useAuth();

  const {
    tripDetails,
    startTime,
    vehicleDetails,
    driverDetails,
    assistantDetails,
  } = data;

  const tripTypeColors = [
    { background: "#ECFDFF", color: "#06AED4" },
    { background: "#FEF6EE", color: "#B93815" },
    { background: "#F4F3FF", color: "#5925DC" },
    { background: "#E0F2FE", color: "#0C4A6E" },
    { background: "#FFFBEA", color: "#92400E" },
  ];

  const dotMap = [LightBlueDot, OrangeDot, PurpleDot, BlueDot, YellowDot];
  const vehicleIconMap = [
    LightBlueBus,
    OrangeBus,
    PurpleBus,
    BlueBus,
    YellowBus,
  ];
  const driverIconMap = [
    LightBlueDriver,
    OrangeDriver,
    PurpleDriver,
    BlueDriver,
    YellowDriver,
  ];
  const assistantIconMap = [
    LightBlueAssistant,
    OrangeAssistant,
    PurpleAssistant,
    BlueAssistant,
    YellowAssistant,
  ];

  /** Stale / deleted trip types or missing keys: neutral grey, not the first real type color */
  const unknownTripTypeStyle = {
    backgroundColor: "#F2F4F7",
    color: "#667085",
    dot: dotMap[0],
    vehicleIcon: vehicleIconMap[0],
    driverIcon: driverIconMap[0],
    assistantIcon: assistantIconMap[0],
  };

  const tripTypeStyleMap = (tripTypes || []).reduce((acc, type, index) => {
    const style = {
      backgroundColor: tripTypeColors[index]?.background || "#FFFFFF",
      color: tripTypeColors[index]?.color || "#000000",
      dot: dotMap[index],
      vehicleIcon: vehicleIconMap[index],
      driverIcon: driverIconMap[index],
      assistantIcon: assistantIconMap[index],
    };
    const key = type.id;
    if (key != null && key !== "") {
      acc[key] = style;
      acc[String(key)] = style;
      const n = Number(key);
      if (!Number.isNaN(n)) acc[n] = style;
    }
    return acc;
  }, {});

  const tripTypeKey = tripDetails.tripType;
  const matchedFromMap =
    tripTypeKey != null &&
    tripTypeKey !== "" &&
    (tripTypeStyleMap[tripTypeKey] ?? tripTypeStyleMap[String(tripTypeKey)]);

  const resolvedStyle = matchedFromMap ?? unknownTripTypeStyle;
  const useGreyPalette = !matchedFromMap;

  const iconImgStyle = useGreyPalette
    ? { opacity: 0.72, filter: "grayscale(1)" }
    : undefined;

  const {
    backgroundColor,
    color,
    dot,
    driverIcon,
    vehicleIcon,
    assistantIcon,
  } = resolvedStyle;

  function formatAMPM(time24) {
    const [hourStr, minuteStr] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || "00";
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  }

  return (
    <>
      <div
        className="calendar-data-item"
        style={{ background: backgroundColor, color }}
        onClick={onClick}
      >
        <div className="calendar-data-item-title">
          <span className="trip-name">
            {compact && (
              <img
                src={dot}
                alt=""
                style={{ marginRight: "0.3rem", ...iconImgStyle }}
              />
            )}
            {tripDetails.tripName}
          </span>
          <span className="trip-time">| {formatAMPM(startTime)}</span>
        </div>

        {!compact && (
          <div className="calendar-data-item-details">
            <span>
              <img
                width={28}
                height={28}
                src={driverIcon}
                alt=""
                style={{ marginRight: "0.3rem", ...iconImgStyle }}
              />
              <div>{driverDetails.driverName}</div>
            </span>
            <span>
              <img
                width={28}
                height={28}
                src={vehicleIcon}
                alt=""
                style={{ marginRight: "0.3rem", ...iconImgStyle }}
              />
              <div>{vehicleDetails.vehicleNumber}</div>
            </span>
            <span>
              <img
                width={28}
                height={28}
                src={assistantIcon}
                alt=""
                style={{ marginRight: "0.3rem", ...iconImgStyle }}
              />
              {assistantDetails?.assistantName ? (
                <div>{assistantDetails.assistantName}</div>
              ) : (
                <div style={{ fontWeight: "600" }}>-</div>
              )}
            </span>
          </div>
        )}
      </div>
      {remainingCount > 0 && (
        <div className="calendar-more-items">+{remainingCount}</div>
      )}
    </>
  );
}

CalendarItem.propTypes = {
  data: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  onClick: PropTypes.func,
};
